import {
  CheckCircleFilled,
  CheckCircleOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Tooltip } from "antd";
import { ItemType } from "antd/es/menu/interface";
import classNames from "classnames";
import AvatarInitials from "components/AvatarInitials";
import { useState } from "react";
import { PostCommentData } from "store/posts/postConstants";
import { getFullName } from "store/user/userActions";

type PostCommentDisplayProps = {
  currentComment: PostCommentData;
  commentTimeAgo: string;
  readonly?: boolean;
  loading: "resolve" | "delete" | false; // Only concerned with resolve/delete loading
  saveComment: (resolved: boolean) => Promise<void>; // For resolving
  CURRENT_COMMENT_MENU: ItemType[] | null;
};

const PostCommentDisplay = ({
  currentComment,
  commentTimeAgo,
  readonly,
  loading,
  saveComment,
  CURRENT_COMMENT_MENU,
}: PostCommentDisplayProps) => {
  const [resolveTooltipOpen, setResolveTooltipOpen] = useState<
    boolean | undefined
  >(undefined);
  const [isResolving, setIsResolving] = useState(false); // Track async operation

  const handleResolveClick = async () => {
    setResolveTooltipOpen(false); // Close tooltip immediately
    setIsResolving(true); // Start async operation
    try {
      await saveComment(!currentComment.resolved);
    } finally {
      setIsResolving(false); // End async operation
      // No need for setTimeout here anymore
    }
  };

  const handleTooltipOpenChange = (open: boolean) => {
    // Only allow hover to control tooltip if not currently resolving
    if (!isResolving) {
      setResolveTooltipOpen(open);
    }
  };

  return (
    <>
      <AvatarInitials
        size="small"
        user={{
          ...currentComment,
          picture: currentComment.profile_pic ?? "",
        }}
      />
      <span className="text-xs flex flex-wrap gap-1">
        <span className="font-medium">{getFullName(currentComment)}</span>
        <span className="text-antd-colorTextTertiary ml-1">
          {commentTimeAgo}
        </span>
      </span>
      <span className="[&_button]:text-antd-colorIcon">
        {(!readonly || currentComment.resolved) && (
          <Tooltip
            mouseLeaveDelay={0}
            open={resolveTooltipOpen}
            onOpenChange={handleTooltipOpenChange}
            title={
              readonly
                ? "Resolved"
                : `Mark as ${
                    currentComment.resolved ? "unresolved" : "resolved"
                  }`
            }
          >
            <Button
              type="text"
              size="small"
              className={classNames("ant-btn-small-fix", {
                "!cursor-default": readonly,
              })}
              loading={loading === "resolve"}
              disabled={readonly}
              onClick={handleResolveClick}
              icon={
                currentComment.resolved ? (
                  <CheckCircleFilled className="text-antd-colorSuccess" />
                ) : (
                  <CheckCircleOutlined />
                )
              }
            />
          </Tooltip>
        )}

        {CURRENT_COMMENT_MENU && (
          <Dropdown
            disabled={Boolean(loading)} // Loading here refers to resolve or delete
            trigger={["click"]}
            menu={{ items: CURRENT_COMMENT_MENU }}
          >
            <Button
              loading={loading === "delete"} // Specifically delete loading
              type="text"
              size="small"
              className="ant-btn-small-fix"
              icon={<MoreOutlined />}
            />
          </Dropdown>
        )}
      </span>

      <div className="col-start-2 text-sm whitespace-pre-line break-all">
        {currentComment.body}
      </div>
    </>
  );
};

export default PostCommentDisplay;
